import { Controller } from "@hotwired/stimulus";
import MapResourceLoader from "../services/map_resource_loader.js";

export default class extends Controller {
  static values = {
    coordinates: { type: Array, default: [] },
    zoom: { type: Number, default: 6 },
    clustering: { type: Boolean, default: true },
    heatmap: { type: Array, default: [] },
    gaps: { type: Array, default: [] }
  };

  static targets = ["map"];

  connect() {
    // Ensure the container has proper dimensions
    this.element.style.height = this.element.style.height || "400px";
    this.element.style.minHeight = "300px";
    
    // Start preloading Leaflet resources immediately for better UX
    try {
      MapResourceLoader.preload('leaflet');
    } catch (error) {
      // MapResourceLoader not available yet, will load resources on demand
    }
    
    this.initializeMap();
    
    // Set up intersection observer to refresh map when visible
    this.setupVisibilityObserver();
  }

  disconnect() {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }
    
    if (this.map) {
      this.map.remove();
    }
  }

  // Refresh map when element becomes visible
  refreshMap() {
    if (this.map) {
      this.map.invalidateSize();
    }
  }

  setupVisibilityObserver() {
    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && this.map) {
            // Map is visible, refresh it
            setTimeout(() => this.refreshMap(), 100);
          }
        });
      }, { threshold: 0.1 });
      
      this.visibilityObserver.observe(this.element);
    }
  }

  async initializeMap() {
    try {
      // Dynamically load Leaflet resources if not already loaded
      await MapResourceLoader.loadLeaflet();
      
      // Now L should be available
      if (typeof L === 'undefined') {
        return;
      }

      const center = this.calculateCenter();
      
      // Initialize map
      this.map = L.map(this.element, {
        center: center,
        zoom: this.zoomValue,
        zoomControl: true,
        attributionControl: true
      });

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(this.map);

      // Add markers
      this.addMarkers();
      
      // Add heatmap if data exists
      if (this.hasHeatmapValue && this.heatmapValue.length > 0) {
        this.addHeatmap();
      }

      // Add gap markers if data exists
      if (this.hasGapsValue && this.gapsValue.length > 0) {
        this.addGapMarkers();
      }

      // Fit map to all data optimally
      this.fitMapToAllData();

    } catch (error) {
      // Error initializing OpenStreetMap
    }
  }

  calculateCenter() {
    // Prioritize heatmap data for centering
    if (this.hasHeatmapValue && this.heatmapValue.length > 0) {
      const bounds = L.latLngBounds();
      this.heatmapValue.forEach(point => {
        const lat = point.lat || point.latitude;
        const lng = point.lng || point.longitude;
        if (lat && lng) {
          bounds.extend([lat, lng]);
        }
      });
      if (!bounds.isEmpty()) {
        return bounds.getCenter();
      }
    }

    // Fall back to coordinates
    if (this.coordinatesValue.length > 0) {
      const coords = this.coordinatesValue.filter(coord => {
        const lat = coord.lat || coord.latitude;
        const lng = coord.lng || coord.longitude;
        return lat && lng;
      });
      
      if (coords.length > 0) {
        const totalLat = coords.reduce((sum, coord) => sum + (coord.lat || coord.latitude), 0);
        const totalLng = coords.reduce((sum, coord) => sum + (coord.lng || coord.longitude), 0);
        return [totalLat / coords.length, totalLng / coords.length];
      }
    }

    // Fall back to gaps
    if (this.hasGapsValue && this.gapsValue.length > 0) {
      const gaps = this.gapsValue.filter(gap => {
        const lat = gap.lat || gap.latitude;
        const lng = gap.lng || gap.longitude;
        return lat && lng;
      });
      
      if (gaps.length > 0) {
        const totalLat = gaps.reduce((sum, gap) => sum + (gap.lat || gap.latitude), 0);
        const totalLng = gaps.reduce((sum, gap) => sum + (gap.lng || gap.longitude), 0);
        return [totalLat / gaps.length, totalLng / gaps.length];
      }
    }

    // Default center (Ghana)
    return [5.5600, -0.2057];
  }

  addMarkers() {
    this.markers = [];
    
    this.coordinatesValue.forEach((coord, index) => {
      // Support both lat/lng and latitude/longitude formats
      const lat = coord.lat || coord.latitude;
      const lng = coord.lng || coord.longitude;
      
      if (lat && lng) {
        const marker = L.marker([lat, lng])
          .bindPopup(this.createPopupContent(coord))
          .addTo(this.map);
        
        this.markers.push(marker);
      }
    });
  }

  addHeatmap() {
    if (typeof L.heatLayer === 'undefined') {
      return;
    }

    const heatmapData = this.heatmapValue.map(point => {
      const lat = point.lat || point.latitude;
      const lng = point.lng || point.longitude;
      return [lat, lng, point.weight || 1];
    }).filter(point => point[0] && point[1]); // Filter out invalid coordinates
    
    if (heatmapData.length > 0) {
      this.heatmap = L.heatLayer(heatmapData, {
        radius: 25,
        blur: 15,
        maxZoom: 10
      }).addTo(this.map);
    }
  }

  addGapMarkers() {
    this.gapMarkers = [];
    
    this.gapsValue.forEach((gap, index) => {
      const lat = gap.lat || gap.latitude;
      const lng = gap.lng || gap.longitude;
      
      if (lat && lng) {
        const marker = L.circleMarker([lat, lng], {
          radius: 8,
          fillColor: "#f59e0b",
          color: "#d97706",
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8
        })
        .bindPopup(`<strong>Coverage Gap</strong><br>Lat: ${lat.toFixed(6)}<br>Lng: ${lng.toFixed(6)}`)
        .addTo(this.map);
        
        this.gapMarkers.push(marker);
      }
    });
  }

  createPopupContent(coord) {
    let content = '<div class="p-2">';
    
    if (coord.staff_name) {
      content += `<strong>${coord.staff_name}</strong><br>`;
    }
    
    if (coord.staff_code) {
      content += `Code: ${coord.staff_code}<br>`;
    }
    
    if (coord.activity) {
      content += `Activity: ${coord.activity}<br>`;
    }
    
    if (coord.status) {
      content += `Status: ${coord.status}<br>`;
    }
    
    if (coord.address) {
      content += `Address: ${coord.address}<br>`;
    }
    
    if (coord.completed_at) {
      content += `Completed: ${coord.completed_at}<br>`;
    }
    
    const latitude = typeof coord.lat === 'number' ? coord.lat : (typeof coord.latitude === 'number' ? coord.latitude : null);
    const longitude = typeof coord.lng === 'number' ? coord.lng : (typeof coord.longitude === 'number' ? coord.longitude : null);
    if (latitude !== null && longitude !== null) {
      content += `Coordinates: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    } else {
      content += `Coordinates: N/A`;
    }
    content += '</div>';
    
    return content;
  }

  fitMapToMarkers() {
    if (this.markers && this.markers.length > 0) {
      const group = new L.featureGroup(this.markers);
      this.map.fitBounds(group.getBounds().pad(0.1));
    }
  }

  fitMapToAllData() {
    const bounds = L.latLngBounds();
    let hasData = false;

    // Add heatmap points to bounds
    if (this.hasHeatmapValue && this.heatmapValue.length > 0) {
      this.heatmapValue.forEach(point => {
        const lat = point.lat || point.latitude;
        const lng = point.lng || point.longitude;
        if (lat && lng) {
          bounds.extend([lat, lng]);
          hasData = true;
        }
      });
    }

    // Add coordinates to bounds
    if (this.coordinatesValue.length > 0) {
      this.coordinatesValue.forEach(coord => {
        const lat = coord.lat || coord.latitude;
        const lng = coord.lng || coord.longitude;
        if (lat && lng) {
          bounds.extend([lat, lng]);
          hasData = true;
        }
      });
    }

    // Add gaps to bounds
    if (this.hasGapsValue && this.gapsValue.length > 0) {
      this.gapsValue.forEach(gap => {
        const lat = gap.lat || gap.latitude;
        const lng = gap.lng || gap.longitude;
        if (lat && lng) {
          bounds.extend([lat, lng]);
          hasData = true;
        }
      });
    }

    // Fit bounds if we have data
    if (hasData && !bounds.isEmpty()) {
      this.map.fitBounds(bounds, { 
        padding: [80, 80],
        maxZoom: 12
      });
    }
  }

  // Public methods for external control
  addMarker(lat, lng, popupContent = null) {
    const marker = L.marker([lat, lng]);
    if (popupContent) {
      marker.bindPopup(popupContent);
    }
    marker.addTo(this.map);
    this.markers.push(marker);
    return marker;
  }

  removeMarker(marker) {
    if (marker && this.markers.includes(marker)) {
      this.map.removeLayer(marker);
      this.markers = this.markers.filter(m => m !== marker);
    }
  }

  setView(lat, lng, zoom = null) {
    this.map.setView([lat, lng], zoom || this.zoomValue);
  }
}
